import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useNavigate } from "react-router";
import {
  Dialog,
  Heading,
  Menu,
  MenuItem,
  MenuTrigger,
  Modal,
  ModalOverlay,
  Popover,
  Separator,
  SubmenuTrigger,
} from "react-aria-components";
import {
  ChevronLeft,
  ChevronRight,
  CornerUpRight,
  Ellipsis,
  ExternalLink,
  FolderPlus,
  Globe,
  Link as LinkIcon,
  Pause,
  Play,
  Plus,
  Sparkles,
  Trash2,
  TrendingUp,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import ChatMarkdown from "@/app/routes/dashboard/components/ChatMarkdown";
import RemixMark from "@/app/routes/dashboard/components/icons/RemixMark";
import PlatformGlyph from "@/app/routes/dashboard/components/PlatformGlyph";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { posterFrameUrl } from "@/app/routes/dashboard/content-calendar/videoThumbnails";
import { compactTrendNum, timeAgo } from "@/app/routes/dashboard/trends/components/trendMeta";
import { queue } from "@/components/Toast";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useGrowFromCard } from "@/lib/growFromCard";
import type { Brand } from "@/hooks/useGetBrands";
import { addedByClient, type Reference, type ReferenceCollection } from "@/types/reference.type";
import { KitTabIndicator } from "../../components/kit/TabIndicator";
import { cn } from "@/utils/helpers";
import { useReferenceDetail } from "../api/useReferences";
import {
  durationLabel,
  openUrl,
  recreatePrompt,
  referenceDomain,
  TYPE_LABEL,
  trendPath,
} from "./referenceMeta";
import { parseAnalysis, type AnalysisScene } from "./referenceAnalysis";
import { ReferenceBrandPickerPanel } from "./ReferenceBrandPicker";
import ConfirmDelete from "./ConfirmDelete";

/*
 * References / Detail (library, style B). A sheet, radius 16, on the scrim:
 * the stage on the left (the media at its own shape on the recess), the info
 * on the right. The top bar walks the wall (J and K); the body reads the why
 * first, then at a glance, the collections, the move to take, the numbers and
 * the full analysis in its parts. Recreate leads the action bar; everything
 * else sits behind More. A reference with no picture opens as the narrower
 * reading sheet with no stage.
 */

const NOTE_MAX = 600;

export interface ReferenceDetailNav {
  index: number;
  count: number;
  onPrevious: (() => void) | null;
  onNext: (() => void) | null;
}

export interface ReferenceDetailProps {
  reference: Reference | null;
  brands: Brand[];
  collections: ReferenceCollection[];
  /** May change it: write the why, move it, collect it, delete it. */
  canEdit: boolean;
  /** May delete it (a client deletes only their own). */
  canDelete: boolean;
  allowEveryone: boolean;
  /** Open with the why editor ready (Add why it's here, Edit on a suggestion). */
  editWhy?: boolean;
  nav?: ReferenceDetailNav;
  onClose: () => void;
  onOpenCollection: (collectionId: string) => void;
  onCollect: (reference: Reference, anchor: HTMLElement) => void;
  onUpdate: (input: { referenceId: string; userNote?: string; suggestion?: "accept" | "dismiss" }) => void;
  onMove: (reference: Reference, brandId: string | null) => void;
  onDelete: (reference: Reference) => void;
}

function formatDate(iso: string) {
  return new Date(iso)
    .toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    .toUpperCase();
}

function formatShortDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toUpperCase();
}

/* ------------------------------------------------------------------ stage */

function VideoStage({
  src,
  poster,
  videoRef,
  onTime,
}: {
  src: string;
  poster: string | null;
  videoRef: RefObject<HTMLVideoElement | null>;
  onTime: (seconds: number) => void;
}) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(0);

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play();
    else video.pause();
  };

  return (
    <div className="ref-stage-media">
      <video
        ref={videoRef}
        src={posterFrameUrl(src)}
        poster={poster ?? undefined}
        muted={muted}
        playsInline
        preload="metadata"
        onClick={toggle}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(e) => setLength(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => {
          setTime(e.currentTarget.currentTime);
          onTime(e.currentTarget.currentTime);
        }}
        className="size-full object-contain"
      />
      {!playing ? (
        <button type="button" className="ref-stage-play" aria-label="Play" onClick={toggle}>
          <Play aria-hidden className="size-5" fill="currentColor" />
        </button>
      ) : null}
      <div className="ref-stage-scrub">
        <button type="button" aria-label={playing ? "Pause" : "Play"} onClick={toggle} className="text-[var(--co-media-ink)]">
          {playing ? <Pause aria-hidden className="size-3.5" /> : <Play aria-hidden className="size-3.5" />}
        </button>
        <span>{durationLabel(time * 1000)}</span>
        <input
          type="range"
          min={0}
          max={length || 1}
          step={0.1}
          value={time}
          aria-label="Seek"
          onChange={(e) => {
            const video = videoRef.current;
            if (video) video.currentTime = Number(e.target.value);
          }}
          className="ref-stage-track"
          style={{ ["--ref-progress" as string]: `${length ? (time / length) * 100 : 0}%` }}
        />
        <span>{durationLabel(length * 1000)}</span>
        <button type="button" aria-label={muted ? "Sound on" : "Sound off"} aria-pressed={!muted} onClick={() => setMuted((m) => !m)} className="text-[var(--co-media-ink)]">
          {muted ? <VolumeX aria-hidden className="size-3.5" /> : <Volume2 aria-hidden className="size-3.5" />}
        </button>
      </div>
    </div>
  );
}

function CarouselStage({ reference }: { reference: Reference }) {
  const detail = useReferenceDetail(reference.id);
  const slides = detail.data?.carousel?.slides ?? [];
  const total = reference.carousel?.total ?? slides.length;
  const [index, setIndex] = useState(0);
  const slide = slides[index];
  const still = slide?.imageUrl ?? (index === 0 ? (reference.imageUrl ?? reference.thumbnailUrl) : null);

  return (
    <>
      <div className="ref-stage-media ref-stage-media--square">
        {still ? (
          <img src={still} alt={`Slide ${index + 1} of ${total}`} referrerPolicy="no-referrer" className="size-full object-contain" />
        ) : (
          <span className="grid size-full place-items-center text-[13px] text-[var(--co-media-ink)] opacity-70">
            {detail.isLoading ? "Reading the slides" : "This slide was not captured"}
          </span>
        )}
        <button
          type="button"
          className="ref-stage-arrow ref-stage-arrow--prev"
          aria-label="Previous slide"
          disabled={index === 0}
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
        >
          <ChevronLeft aria-hidden className="size-4" />
        </button>
        <button
          type="button"
          className="ref-stage-arrow ref-stage-arrow--next"
          aria-label="Next slide"
          disabled={index >= total - 1}
          onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
        >
          <ChevronRight aria-hidden className="size-4" />
        </button>
        <span className="ref-stage-count">
          {index + 1} / {total}
        </span>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- why */

function WhySection({
  reference,
  canEdit,
  startEditing,
  onSave,
  onSuggestion,
}: {
  reference: Reference;
  canEdit: boolean;
  startEditing: boolean;
  onSave: (note: string) => void;
  onSuggestion: (decision: "accept" | "dismiss") => void;
}) {
  const [editing, setEditing] = useState(startEditing && canEdit);
  const [draft, setDraft] = useState(reference.userNote ?? "");

  if (editing) {
    return (
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-2">
          <span className="ref-field-label">Why it's here</span>
          <textarea
            autoFocus
            value={draft}
            maxLength={NOTE_MAX}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Say what to take from it: the move, the feel, the line."
            className="ref-field ref-field--area co-glass-control"
          />
          <span className="text-[12.5px] leading-[1.45] text-co-faint">
            Shown on the tile, for everyone who can see this brand.
          </span>
        </label>
        <div className="flex items-center justify-between">
          {reference.suggestedNote ? (
            <button type="button" className="ref-text-action" onClick={() => setDraft(reference.suggestedNote ?? "")}>
              <Sparkles aria-hidden className="size-3.5" />
              Suggest
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="ref-btn ref-btn--secondary co-glass-control"
              onClick={() => {
                setDraft(reference.userNote ?? "");
                setEditing(false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ref-btn ref-btn--primary"
              disabled={draft.trim() === (reference.userNote ?? "").trim()}
              onClick={() => {
                onSave(draft.trim());
                setEditing(false);
              }}
            >
              Save why
            </button>
          </div>
        </div>
      </div>
    );
  }

  const by = reference.userNoteBy?.name ?? reference.createdBy?.name ?? null;
  const when = reference.userNoteUpdatedAt ?? reference.createdAt;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <span className="ref-mono-label">Why it's here</span>
        {canEdit && reference.userNote ? (
          <button type="button" className="ref-mono-action" onClick={() => setEditing(true)}>
            Edit
          </button>
        ) : null}
      </div>
      {reference.userNote ? (
        <>
          <p className="ref-detail-why">{reference.userNote}</p>
          <p className="ref-mono-faint">
            Added by {by ?? (addedByClient(reference) ? "the client" : "the team")}
            {addedByClient(reference) ? " · client" : ""} · {formatShortDate(when)}
          </p>
        </>
      ) : reference.suggestedNote && canEdit ? (
        <div className="ref-detail-suggested">
          <span className="ref-suggested-label">
            <Sparkles aria-hidden className="size-3 text-co-accent" />
            <span><span className="normal-case">Creo</span> suggests a why</span>
          </span>
          <p className="ref-detail-why text-co-text2">{reference.suggestedNote}</p>
          <span className="ref-suggested-actions">
            <button type="button" className="text-co-accent-ink" onClick={() => onSuggestion("accept")}>
              Accept
            </button>
            <button
              type="button"
              className="text-co-text2"
              onClick={() => {
                setDraft(reference.suggestedNote ?? "");
                setEditing(true);
              }}
            >
              Edit
            </button>
            <button type="button" className="text-co-muted" onClick={() => onSuggestion("dismiss")}>
              Dismiss
            </button>
          </span>
        </div>
      ) : canEdit ? (
        <button type="button" className="ref-add-why" onClick={() => setEditing(true)}>
          <Plus aria-hidden className="size-[13px]" />
          Add why it's here
        </button>
      ) : (
        <p className="ref-detail-why text-co-faint">No note yet</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- analysis */

/** A part's timestamped rows. One highlight slides to the row that is playing,
 *  rather than each row switching its own background. */
function SceneList({
  scenes,
  playingAt,
  onSeek,
}: {
  scenes: AnalysisScene[];
  playingAt: number | null;
  onSeek: ((seconds: number) => void) | null;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const hlRef = useRef<HTMLSpanElement>(null);
  const placed = useRef(false);
  const lit = scenes.findIndex((scene, i) => {
    const end = scene.end ?? scenes[i + 1]?.start ?? Infinity;
    return playingAt !== null && playingAt >= scene.start && playingAt < end;
  });

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const hl = hlRef.current;
    if (!wrap || !hl) return;
    const place = () => {
      const row = lit >= 0 ? wrap.querySelectorAll<HTMLElement>("li")[lit] : undefined;
      if (!row) {
        hl.style.opacity = "0";
        return;
      }
      // The first placement lands in place; only later moves slide.
      if (!placed.current) hl.style.transition = "none";
      hl.style.height = `${row.offsetHeight}px`;
      hl.style.transform = `translateY(${row.offsetTop}px)`;
      hl.style.opacity = "1";
      if (!placed.current) {
        placed.current = true;
        void hl.offsetHeight;
        hl.style.transition = "";
      }
    };
    place();
    const watch = new ResizeObserver(place);
    watch.observe(wrap);
    return () => watch.disconnect();
  }, [lit, scenes]);

  return (
    <div ref={wrapRef} className="ref-scenes">
      <span ref={hlRef} className="ref-scene-hl" aria-hidden />
      <ul className="flex flex-col">
        {scenes.map((scene, i) => (
          <li key={`${scene.start}-${i}`} className={cn("ref-scene", i === lit && "is-playing")}>
            <button
              type="button"
              className="ref-stamp"
              disabled={!onSeek}
              aria-label={`Play from ${scene.stamp}`}
              onClick={() => onSeek?.(scene.start)}
            >
              {scene.stamp}
            </button>
            <span className="text-[13.5px] leading-[1.45] text-co-text">{scene.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AnalysisParts({
  analysis,
  isTrend,
  playingAt,
  onSeek,
}: {
  analysis: string;
  isTrend: boolean;
  playingAt: number | null;
  onSeek: ((seconds: number) => void) | null;
}) {
  const parsed = useMemo(() => parseAnalysis(analysis), [analysis]);
  const [tab, setTab] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  // A tab press scrolls to its section; until that scroll settles the scroll
  // watcher below must not hand the highlight back to a section it passes.
  const jumpingUntil = useRef(0);

  // The tab that is lit follows what is at the top of the sheet.
  useEffect(() => {
    if (!parsed || parsed.parts.length < 2) return;
    const first = sectionRefs.current[0];
    const scroller = first?.closest<HTMLElement>(".ref-body");
    if (!scroller) return;
    const onScroll = () => {
      if (performance.now() < jumpingUntil.current) return;
      const line = scroller.getBoundingClientRect().top + 90;
      let at = 0;
      sectionRefs.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top <= line) at = i;
      });
      if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4) at = sectionRefs.current.length - 1;
      setTab(at);
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [parsed]);

  const jumpTo = useCallback((i: number) => {
    setTab(i);
    const el = sectionRefs.current[i];
    if (!el) return;
    jumpingUntil.current = performance.now() + 700;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, []);

  if (!parsed) return null;
  const label = isTrend ? "The trend when saved" : "Full analysis";


  return (
    <div className="flex flex-col gap-[22px]">
      <div className="ref-analysis-head">
        <span className="flex items-center gap-3">
          <span className="ref-section-title">{label}</span>
          {parsed.parts.length > 1 ? (
            <span className="ref-mono-faint">
              {parsed.kind} · {parsed.parts.length} parts
            </span>
          ) : null}
        </span>
      </div>
      <div className="flex flex-col gap-[22px]">
          {parsed.parts.length > 1 ? (
            <div className="ref-parts ref-parts--sticky" role="tablist" aria-label={label} data-indicator>
              {parsed.parts.map((p, i) => (
                <button
                  key={p.key}
                  type="button"
                  role="tab"
                  aria-selected={i === tab}
                  className={cn("ref-part", i === tab && "is-on")}
                  onClick={() => jumpTo(i)}
                >
                  {p.label}
                  {p.count ? ` ${p.count}` : ""}
                </button>
              ))}
              <KitTabIndicator selectedKey={tab} />
            </div>
          ) : null}
          {parsed.parts.map((current, i) => (
            <div
              key={current.key}
              ref={(el) => {
                sectionRefs.current[i] = el;
              }}
              className="ref-part-section flex flex-col gap-2"
              role="region"
              aria-label={current.label}
            >
              <div className="flex items-center justify-between">
                <h3 className="ref-part-heading">{current.label}</h3>
                {current.scenes?.length ? (
                  <span className="ref-mono-faint">
                    {current.scenes.length}
                    {onSeek ? " · press a time to play it" : ""}
                  </span>
                ) : null}
              </div>
              {current.scenes?.length ? (
                <SceneList scenes={current.scenes} playingAt={playingAt} onSeek={onSeek} />
              ) : current.markdown ? (
                <ChatMarkdown className="streamdown-doc ref-analysis-md" content={current.markdown} />
              ) : (
                <p className="text-[13.5px] text-co-faint">{current.emptyLine ?? "Nothing in this part."}</p>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- the sheet */

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-[3px]">
      <span className="ref-mono-faint">{label}</span>
      <span className="ref-stat-value">{value}</span>
    </div>
  );
}

export default function ReferenceDetail(props: ReferenceDetailProps) {
  const portal = useCoShellPortal();
  const { reference, onClose } = props;
  /* Card → detail, from sm up: the sheet grows out of the reference's picture
     on the wall and, closing, goes back into the picture of the one it shows
     then. On a phone it stays a sheet rising from the foot. The last open
     reference is kept, because closing has already cleared it. */
  const desk = useMediaQuery("(min-width: 640px)");
  const lastId = useRef(reference?.id ?? null);
  useLayoutEffect(() => {
    if (reference) lastId.current = reference.id;
  }, [reference]);
  const cardOf = useCallback(() => {
    const id = lastId.current;
    return id ? document.querySelector(`[data-reference-id="${CSS.escape(id)}"] .ref-media`) : null;
  }, []);
  const growRef = useGrowFromCard(desk ? cardOf : undefined, !!reference);
  // Closing clears `reference` at once; the last one stays on screen for the
  // close animation, so the sheet shrinks into the card with its content.
  const [kept, setKept] = useState(reference);
  if (reference && reference !== kept) setKept(reference);
  const shown = reference ?? kept;
  return (
    <ModalOverlay
      isOpen={!!reference}
      onOpenChange={(open) => !open && onClose()}
      isDismissable
      UNSTABLE_portalContainer={portal ?? undefined}
      className={({ isEntering, isExiting }) =>
        cn(
          "fixed inset-0 z-[var(--z-index-modal)] flex items-center justify-center bg-co-scrim p-6 max-sm:items-end max-sm:p-2",
          isEntering && "animate-in-smooth fade-in",
          isExiting && "animate-out-slow fade-out",
        )
      }
    >
      <Modal
        ref={desk ? growRef : undefined}
        className={({ isEntering, isExiting }) =>
          cn(
            "outline-none",
            desk && "co-from-card",
            !desk && isEntering && "animate-in-smooth sheet-in",
            !desk && isExiting && "animate-out-slow sheet-out",
          )
        }
      >
        {/* Keyed: walking to the next reference starts its sheet fresh. */}
        {shown ? <Sheet key={shown.id} {...props} reference={shown} /> : null}
      </Modal>
    </ModalOverlay>
  );
}

function Sheet({
  reference,
  brands,
  collections,
  canEdit,
  canDelete,
  allowEveryone,
  editWhy = false,
  nav,
  onClose,
  onOpenCollection,
  onCollect,
  onUpdate,
  onMove,
  onDelete,
}: ReferenceDetailProps & { reference: Reference }) {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playingAt, setPlayingAt] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const moreRef = useRef<HTMLButtonElement | null>(null);

  const picture = reference.imageUrl ?? reference.thumbnailUrl;
  const isCarousel = (reference.carousel?.total ?? 0) > 1;
  const link = openUrl(reference);
  const domain = referenceDomain(reference);
  const toTrend = trendPath(reference);
  const metrics = reference.metrics ?? {};
  const inCollections = collections.filter((c) => (reference.collectionIds ?? []).includes(c.id));
  const palette = reference.palette ?? [];
  const mood = reference.mood ?? [];
  const glance = palette.length > 0 || !!reference.hook || mood.length > 0 || !!reference.shot;
  const theMove = reference.theMove ?? null;
  const whatItIs = reference.whatItIs ?? (theMove ? null : reference.summary);

  /* J and K walk the wall, as on Trends; never while typing. */
  useEffect(() => {
    if (!nav) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "j" || e.key === "ArrowRight") nav.onNext?.();
      if (e.key === "k" || e.key === "ArrowLeft") nav.onPrevious?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav]);

  const seek = reference.videoUrl
    ? (seconds: number) => {
        const video = videoRef.current;
        if (!video) return;
        video.currentTime = seconds;
        void video.play();
      }
    : null;

  const copyLink = async () => {
    const url = `${window.location.origin}/dashboard/references?ref=${encodeURIComponent(reference.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      queue.add({ title: "Link copied", variant: "success" });
    } catch {
      queue.add({ title: "Could not copy the link", variant: "error" });
    }
  };

  return (
    <Dialog
      aria-label={reference.title}
      className="ref-sheet outline-none"
    >
      {/* The stage is always here, so a reference with no media keeps the same
          sheet: the media box is just empty. */}
      <div className="ref-stage">
        {isCarousel ? (
          <CarouselStage reference={reference} />
        ) : reference.videoUrl ? (
          <VideoStage src={reference.videoUrl} poster={picture} videoRef={videoRef} onTime={setPlayingAt} />
        ) : picture ? (
          <div className="ref-stage-media">
            <img src={picture} alt="" referrerPolicy="no-referrer" className="size-full object-contain" />
          </div>
        ) : (
          // The wall's own placeholder: the reference's type, where the media would be.
          <div className="ref-stage-media" aria-hidden>
            <div className="ref-placeholder">
              <span className="ref-placeholder-label">{TYPE_LABEL[reference.type]}</span>
            </div>
          </div>
        )}
      </div>

      <div className="ref-info">
        <div className="ref-topbar">
          {nav ? (
            <div className="flex items-center gap-2">
              <button type="button" className="ref-walk co-glass-control" aria-label="Previous reference" disabled={!nav.onPrevious} onClick={() => nav.onPrevious?.()}>
                <ChevronLeft aria-hidden className="size-[15px]" />
              </button>
              <button type="button" className="ref-walk co-glass-control" aria-label="Next reference" disabled={!nav.onNext} onClick={() => nav.onNext?.()}>
                <ChevronRight aria-hidden className="size-[15px]" />
              </button>
              <span className="ref-mono text-co-muted">
                {nav.index + 1} of {nav.count}
              </span>
              <span className="ref-mono-faint max-sm:hidden" aria-hidden>
                J · K
              </span>
            </div>
          ) : (
            <span />
          )}
          <button type="button" className="ref-icon-close" aria-label="Close" onClick={onClose}>
            <X aria-hidden className="size-3.5" />
          </button>
        </div>

        <div className="ref-body custom-scrollbar">
          <Heading slot="title" className="ref-detail-title">
            {reference.title}
          </Heading>
          <p className="ref-source">
            {reference.platform === "web" || (!reference.platform && domain) ? (
              <Globe aria-hidden className="size-3 text-co-muted" />
            ) : reference.platform ? (
              <PlatformGlyph platform={reference.platform.toUpperCase()} className="size-3 text-co-muted" />
            ) : null}
            {[
              reference.authorHandle ? <span key="h" className="text-co-text2">@{reference.authorHandle}</span> : null,
              domain ? <span key="d">{domain}</span> : null,
              reference.postedAt ? <span key="p">Posted {formatDate(reference.postedAt)}</span> : null,
            ]
              .filter(Boolean)
              .flatMap((node, i) => (i === 0 ? [node] : [<span key={`s${i}`} className="text-co-faint">·</span>, node]))}
          </p>

          <WhySection
            key={`${editWhy}`}
            reference={reference}
            canEdit={canEdit}
            startEditing={editWhy}
            onSave={(note) => onUpdate({ referenceId: reference.id, userNote: note })}
            onSuggestion={(decision) => onUpdate({ referenceId: reference.id, suggestion: decision })}
          />

          {glance ? (
            <section className="ref-glance">
              <span className="ref-mono-label">At a glance</span>
              <div className="ref-glance-grid">
                {palette.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <span className="ref-mono-faint">Palette</span>
                    <span className="flex flex-wrap gap-1">
                      {palette.map((hex) => (
                        <span key={hex} className="ref-swatch" style={{ background: hex }} title={hex} />
                      ))}
                    </span>
                  </div>
                ) : null}
                {reference.hook ? (
                  <div className="flex flex-col gap-2">
                    <span className="ref-mono-faint">Hook</span>
                    <p className="ref-glance-text">{reference.hook}</p>
                  </div>
                ) : null}
                {mood.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <span className="ref-mono-faint">Mood</span>
                    <span className="flex flex-wrap gap-1.5">
                      {mood.map((m) => (
                        <span key={m} className="ref-mood">{m}</span>
                      ))}
                    </span>
                  </div>
                ) : null}
                {reference.shot ? (
                  <div className="flex flex-col gap-2">
                    <span className="ref-mono-faint">Shot</span>
                    <p className="ref-glance-text">{reference.shot}</p>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {inCollections.length > 0 || canEdit ? (
            <section className="flex flex-col gap-2.5">
              <span className="ref-mono-label">In collections</span>
              <div className="flex flex-wrap items-center gap-2">
                {inCollections.map((c) => {
                  const thumb = c.cover.find((item) => item.imageUrl)?.imageUrl;
                  return (
                    <button key={c.id} type="button" className="ref-chip-sm co-glass-control" onClick={() => onOpenCollection(c.id)}>
                      {thumb ? <img src={thumb} alt="" className="size-6 rounded-full object-cover" /> : <span className="size-1" />}
                      {c.name}
                    </button>
                  );
                })}
                {canEdit ? (
                  <button type="button" className="ref-text-action" onClick={(e) => onCollect(reference, e.currentTarget)}>
                    <Plus aria-hidden className="size-[13px]" />
                    Add to a collection
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}

          {theMove || whatItIs ? (
            <section className="flex flex-col gap-3">
              {theMove ? (
                <>
                  <span className="ref-mono-label text-co-accent-ink">The move to take</span>
                  <p className="ref-move">{theMove}</p>
                </>
              ) : null}
              {whatItIs ? (
                theMove ? (
                  <div className="flex gap-3.5 pt-1">
                    <span className="ref-mono-label w-[84px] shrink-0">What it is</span>
                    <p className="text-[13.5px] leading-[1.5] text-co-text2">{whatItIs}</p>
                  </div>
                ) : (
                  <p className="text-[14px] leading-[1.55] text-co-text2">{whatItIs}</p>
                )
              ) : null}
            </section>
          ) : null}

          <div className="ref-stats">
            {metrics.views != null ? <Stat label="Views" value={compactTrendNum(metrics.views)} /> : null}
            {metrics.likes != null ? <Stat label="Likes" value={compactTrendNum(metrics.likes)} /> : null}
            {metrics.comments != null ? <Stat label="Comments" value={compactTrendNum(metrics.comments)} /> : null}
            <Stat label="Saved" value={timeAgo(reference.createdAt)} />
          </div>

          {reference.analysis ? (
            <AnalysisParts
              key={reference.id}
              analysis={reference.analysis}
              isTrend={reference.type === "trend"}
              playingAt={playingAt}
              onSeek={seek}
            />
          ) : null}

          {reference.caption ? (
            <section className="flex flex-col gap-1.5">
              <span className="ref-mono-label">Original text</span>
              <p className="whitespace-pre-wrap text-[13.5px] leading-[1.55] text-co-muted">{reference.caption}</p>
            </section>
          ) : null}
        </div>

        <div className="ref-actionbar">
          <div className="flex min-w-0 flex-wrap items-center gap-2.5">
            <button
              type="button"
              className="ref-btn-lg ref-btn--primary"
              onClick={() => navigate("/dashboard/workshop", { state: { draftPrompt: recreatePrompt(reference) } })}
            >
              <RemixMark aria-hidden className="size-[15px]" />
              Recreate
            </button>
            {toTrend ? (
              <button type="button" className="ref-btn-lg co-glass-control text-co-text" onClick={() => navigate(toTrend)}>
                <TrendingUp aria-hidden className="size-3.5" />
                Open trend
              </button>
            ) : null}
            {link ? (
              <a href={link} target="_blank" rel="noreferrer noopener" className="ref-btn-lg co-glass-control text-co-text">
                <ExternalLink aria-hidden className="size-3.5" />
                {reference.type === "trend" ? "Open example" : "Open original"}
              </a>
            ) : null}
          </div>
          <MenuTrigger>
            <button ref={moreRef} type="button" className="ref-more co-glass-control" aria-label="More">
              <Ellipsis aria-hidden className="size-4" />
            </button>
            <Popover placement="top end" offset={8} className="ref-chrome-pop w-[240px]">
              <Menu
                className="flex flex-col p-1.5 outline-none"
                onAction={(key) => {
                  if (key === "collect" && moreRef.current) onCollect(reference, moreRef.current);
                  if (key === "copy") void copyLink();
                  if (key === "delete") setConfirmDelete(true);
                }}
              >
                {canEdit ? (
                  <SubmenuTrigger>
                    <MenuItem id="move" className="ref-menu-row" textValue="Move to a brand">
                      <CornerUpRight aria-hidden className="size-3.5" />
                      <span className="flex-1">Move to a brand…</span>
                      <ChevronRight aria-hidden className="size-3.5 text-co-muted" />
                    </MenuItem>
                    <Popover placement="end top" offset={6} className="ref-chrome-pop w-[304px]">
                      <Dialog aria-label="Move to a brand" className="outline-none">
                        {({ close }) => (
                          <ReferenceBrandPickerPanel
                            label="Move to"
                            brands={brands}
                            value={reference.brandId}
                            hereId={reference.brandId}
                            allowEveryone={allowEveryone}
                            onPick={(brandId) => {
                              close();
                              onMove(reference, brandId);
                            }}
                          />
                        )}
                      </Dialog>
                    </Popover>
                  </SubmenuTrigger>
                ) : null}
                {canEdit ? (
                  <MenuItem id="collect" className="ref-menu-row" textValue="Add to a collection">
                    <FolderPlus aria-hidden className="size-3.5" />
                    Add to a collection
                  </MenuItem>
                ) : null}
                <MenuItem id="copy" className="ref-menu-row" textValue="Copy link">
                  <LinkIcon aria-hidden className="size-3.5" />
                  Copy link
                </MenuItem>
                {canDelete ? (
                  <>
                    <Separator className="my-1 h-px bg-co-line" />
                    <MenuItem id="delete" className="ref-menu-row ref-menu-row--danger" textValue="Delete">
                      <Trash2 aria-hidden className="size-3.5" />
                      Delete…
                    </MenuItem>
                  </>
                ) : null}
              </Menu>
            </Popover>
          </MenuTrigger>
        </div>
      </div>

      <ConfirmDelete
        isOpen={confirmDelete}
        title="Delete this reference?"
        description={`“${reference.title}” leaves the library for everyone, with its analysis and its why, and any strategy slide that uses it loses it.`}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete(reference);
        }}
      />
    </Dialog>
  );
}
